import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { NumericFormat } from 'react-number-format';
import MainCard from 'components/MainCard';
import { withValueCap } from 'pages/globals/global';
import dayjs from 'dayjs';

/**
 * IFRS16FieldsModifications component renders the IFRS 16-Specific Fields and lease
 * changes table for the lease record management.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {Object} props.constantFormValues - Form configuration values
 * @param {function} props.handleInputMonthChange - Function to handle input month changes
 * @param {Object} props.form - Form instance needed for field access and manipulation
 * @param {number} props.rowIndex - Current row index for determination of disabled fields
 *
 * @returns {JSX.Element} The IFRS 16-Specific Fields form section
 */
const IFRS16FieldsModifications = ({ screen, constantFormValues, handleInputMonthChange, form, rowIndex }) => {
	return (
		<Row gutter={[16, 0]}>
			<Col xs={24}>
				<Form.List name="changes">
					{(fields) => {
						return (
							<>
								<MainCard content={false} sx={{ mb: 2, mt: '12px', borderColor: '#d9d9d9' }}>
									<TableContainer
										sx={{
											width: '100%',
											overflowX: 'auto',
											position: 'relative',
											display: 'block',
											'& td, & th': { whiteSpace: 'nowrap' },
											'& td': {
												borderTop: '0px',
												borderLeft: '0px',
												borderBottom: '0px',
											},
											'& td:not(:last-child)': {
												borderRight: '1px solid #e0e0e0',
											},
											'& tr:not(:last-child)': {
												borderBottom: '1px solid #e0e0e0',
											},
											'& th': {
												borderBottom: '1px solid #e0e0e0',
											},
										}}
									>
										<Table
											sx={{
												'& .MuiTableBody-root .MuiTableCell-root': {
													px: 0.5,
													py: 0.5,
												},
												tableLayout: 'fixed',
											}}
										>
											<TableHead>
												<TableRow>
													{constantFormValues?.changes?.orderedRowHeader.map((key) => (
														<TableCell
															key={constantFormValues?.changes?.changeList[key].key}
															sx={{ fontSize: '14px', backgroundColor: '#e1f0ff !important' }}
															align="center"
														>
															{constantFormValues?.changes?.changeList[key].label}
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{fields?.map((field, index) => {
													return (
														<TableRow key={index} style={{ height: '60px' }}>
															<TableCell style={{ position: 'relative' }}>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
																	]}
																	rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
																>
																	<DatePicker
																		disabled={
																			index === 0
																				? true
																				: screen === 'VIEW'
																					? true
																					: screen !== 'CREATE'
																						? index < rowIndex
																							? true
																							: false
																						: false
																		}
																		onChange={(date) => handleInputMonthChange(date, index)}
																		format="YYYY-MM-DD"
																		style={{
																			width: '100%',
																			height: '32px',
																		}}
																		className="antd-datePicker"
																		disabledDate={(current) =>
																			current &&
																			current.isBefore(dayjs(form.getFieldValue('leaseStartDate')), 'day')
																		}
																	/>
																</Form.Item>
															</TableCell>
															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeDescription'].key,
																	]}
																	initialValue={
																		constantFormValues?.changes?.changeList['inputChangeDescription'].options[0]
																			.value
																	}
																>
																	<Select
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={
																			constantFormValues?.changes?.changeList['inputChangeDescription'].options
																		}
																	>
																		{constantFormValues?.changes?.changeList[
																			'inputChangeDescription'
																		].options?.map(({ key, value, label }) => (
																			<Select.Option key={key} value={value}>
																				{label}
																			</Select.Option>
																		))}
																	</Select>
																</Form.Item>
															</TableCell>
															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
																	]}
																>
																	<Input
																		disabled
																		type="number"
																		style={{
																			marginBottom: 0,
																			textAlign: 'center',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeLeaseTerm',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeLeaseTerm',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																	/>
																</Form.Item>
															</TableCell>

															{/* <TableCell align="center">
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeGrossAmount'].key,
																	]}
																>
																	<NumericFormat
																		size="large"
																		decimalScale={2}
																		placeholder="0.00"
																		fixedDecimalScale
																		thousandSeparator=","
																		isAllowed={withValueCap}
																		style={{
																			textAlign: 'right',
																			height: '32px',
																			borderRadius: '4px',
																			fontSize: '13px',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeGrossAmount',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeGrossAmount',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																		customInput={Input}
																		disabled
																	/>
																</Form.Item>
															</TableCell> */}

															<TableCell align="center">
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeDiscountRate'].key,
																	]}
																>
																	<NumericFormat
																		size="large"
																		decimalScale={2}
																		placeholder="0.00"
																		fixedDecimalScale
																		thousandSeparator=","
																		isAllowed={withValueCap}
																		style={{
																			textAlign: 'center',
																			height: '32px',
																			borderRadius: '4px',
																			fontSize: '13px',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeDiscountRate',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeDiscountRate',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																		customInput={Input}
																		disabled={
																			index === 0
																				? true
																				: screen === 'CREATE'
																					? false
																					: screen === 'VIEW'
																						? true
																						: screen !== 'CREATE'
																							? index < rowIndex
																								? true
																								: false
																							: false
																		}
																	/>
																</Form.Item>
															</TableCell>

															{/* <TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeEscalation'].key,
																	]}
																>
																	<Input
																		disabled
																		type="number"
																		style={{
																			marginBottom: 0,
																			textAlign: 'center',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeEscalation',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeEscalation',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																	/>
																</Form.Item>
															</TableCell> */}

															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['paymentFrequency'].key,
																	]}
																>
																	<Select
																		disabled
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
																	>
																		{constantFormValues?.changes?.changeList['paymentFrequency'].options?.map(
																			({ key, value, label }) => (
																				<Select.Option key={key} value={value}>
																					{label}
																				</Select.Option>
																			)
																		)}
																	</Select>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
																>
																	<Select
																		disabled
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={constantFormValues?.changes?.changeList['paymentTime'].options}
																	>
																		{constantFormValues?.changes?.changeList['paymentTime'].options?.map(
																			({ key, value, label }) => (
																				<Select.Option key={key} value={value}>
																					{label}
																				</Select.Option>
																			)
																		)}
																	</Select>
																</Form.Item>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</MainCard>
							</>
						);
					}}
				</Form.List>
			</Col>
		</Row>
	);
};

export default React.memo(IFRS16FieldsModifications);
